import React, { useEffect, useState, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import ApiService, { API_URL } from "../services/api.service";
import AuthService from "../services/auth.service";
import Table from "./components/table.component";
import Modal from "react-responsive-modal";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Swal from "sweetalert2";

const getVoucherUrl = (filename) => {
  const user = AuthService.getCurrentUser();
  if (!user || !user.token) return '';
  return `${API_URL}voucher/${filename}?token=${user.token}`;
};

const Charge = (props) => {
  const [charges, setCharges]           = useState([]);
  const [reviewOpen, setReviewOpen]     = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading]           = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);

  // Pago parcial
  const [showPartialInput, setShowPartialInput] = useState(false);
  const [partialAmount, setPartialAmount]       = useState("");

  // Deudas del cliente (incluye la orden actual), con el monto pendiente real
  // calculado igual que el reparto del backend.
  const [clientDebts, setClientDebts]   = useState([]);
  const [loadingDebts, setLoadingDebts] = useState(false);
  const [debtsError, setDebtsError]     = useState(false);
  // Cliente al que corresponden clientDebts: evita usar por un instante las
  // deudas vacías o del voucher anterior antes de que termine la carga.
  const [debtsUserId, setDebtsUserId]   = useState(null);

  // Cobro masivo
  const [loadingMass, setLoadingMass] = useState(false);

  const handleMassDebt = async () => {
    const confirm = await Swal.fire({
      title: 'Cobro masivo',
      text: '¿Enviar notificación WhatsApp a todos los clientes con deuda activa?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.value) return;
    setLoadingMass(true);
    try {
      const user = AuthService.getCurrentUser();
      const res = await ApiService.massDebtCharge(user.token);
      const generated = res && res.data && res.data.generated != null ? res.data.generated : '?';
      toast.success('Links de cobro generados: ' + generated + '. El envío se procesará automáticamente.', { autoClose: 4000 });
    } catch (e) {
      toast.error('Error al enviar cobro masivo');
    } finally {
      setLoadingMass(false);
    }
  };

  const fetchCharges = async () => {
    setLoadingTable(true);
    try {
      const user = AuthService.getCurrentUser();
      const data = await ApiService.getCharges(user.token);
      setCharges([...data.data.data]);
    } catch (e) {
      // silently fail — table stays empty
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchCharges();
  }, []);

  // ── Revisión secuencial ────────────────────────────────────────────────────

  const currentCharge = charges[currentIndex] || null;

  const openReview = () => {
    setCurrentIndex(0);
    setShowPartialInput(false);
    setPartialAmount("");
    setClientDebts([]);
    setReviewOpen(true);
  };

  const goNext = () => {
    setShowPartialInput(false);
    setPartialAmount("");
    setClientDebts([]);
    if (currentIndex < charges.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setReviewOpen(false);
      toast.success("Revisión completada.", { autoClose: 2000 });
      fetchCharges();
    }
  };

  // Carga deudas cuando cambia el charge actual
  useEffect(() => {
    if (!reviewOpen || !currentCharge) return;

    const idUser = currentCharge.Purchase && currentCharge.Purchase.id_user;
    if (!idUser) return;

    // Si el admin pasa a otro voucher antes de que responda, se descarta la
    // respuesta: si no, se validaría con las deudas de otro cliente.
    let stale = false;
    setLoadingDebts(true);
    setDebtsError(false);
    setClientDebts([]);
    setDebtsUserId(null);
    const user = AuthService.getCurrentUser();
    ApiService.getUserPendingDebts(user.token, idUser)
      .then((res) => {
        if (stale) return;
        setClientDebts((res && res.data && res.data.data) || []);
        setDebtsUserId(idUser);
      })
      .catch(() => {
        if (stale) return;
        setClientDebts([]);
        setDebtsError(true);
      })
      .finally(() => {
        if (!stale) setLoadingDebts(false);
      });

    return () => { stale = true; };
  }, [currentIndex, reviewOpen]);

  // ── Montos para las alertas ────────────────────────────────────────────────
  // El backend reparte el pago desde la deuda más antigua del cliente, no desde
  // la orden del voucher, y "Completo" salda TODAS las deudas sin mirar el monto.

  const round2 = (n) => Math.round(n * 100) / 100;

  const userDebts = currentCharge
    ? clientDebts.filter((p) => p.id !== currentCharge.id_purchase)
    : [];
  const mainDebt = currentCharge
    ? clientDebts.find((p) => p.id === currentCharge.id_purchase)
    : null;
  const mainPending  = mainDebt ? round2(parseFloat(mainDebt.pending) || 0) : 0;
  const totalPending = round2(clientDebts.reduce((s, d) => s + (parseFloat(d.pending) || 0), 0));
  const currentUserId = currentCharge && currentCharge.Purchase ? currentCharge.Purchase.id_user : null;
  const debtsReady    = !loadingDebts && !debtsError && debtsUserId !== null && debtsUserId === currentUserId;

  // Único bloqueo del pago parcial: que supere la deuda total del cliente
  // (el exceso no se registraría en ningún lado). Repartir entre varias
  // órdenes es la autoasignación normal y no pide confirmación.
  const getPartialError = (amount) => {
    if (!(amount > 0) || !debtsReady) return null;
    if (amount > totalPending) {
      return `El monto supera la deuda total del cliente (S/ ${totalPending.toFixed(2)}). El exceso no se registra en ningún lado.`;
    }
    return null;
  };

  // Vista previa del reparto: mismo orden que el backend (de la deuda más
  // antigua a la más nueva).
  const getAllocationPreview = (amount) => {
    if (!(amount > 0) || !debtsReady) return [];
    let remaining = amount;
    const preview = [];
    clientDebts.forEach((d) => {
      if (remaining <= 0) return;
      const pending = round2(parseFloat(d.pending) || 0);
      if (pending <= 0) return;
      const applied = round2(Math.min(remaining, pending));
      preview.push({ id: d.id, applied, left: round2(pending - applied) });
      remaining = round2(remaining - applied);
    });
    return preview;
  };

  const confirmWarnings = async (title, lines) => {
    const result = await Swal.fire({
      title,
      html: lines.map((l) => `<p style="margin:0 0 8px;text-align:left">${l}</p>`).join(''),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Revisar',
    });
    return !!result.value;
  };

  const handleApproveComplete = async () => {
    if (!debtsReady) {
      const ok = await confirmWarnings('No se pudo validar la deuda', [
        'No se cargaron las deudas del cliente. "Listo" marcará como pagadas <b>todas</b> sus órdenes pendientes.',
      ]);
      if (!ok) return;
      return handleApprove('completo');
    }

    handleApprove('completo');
  };

  const handleApprovePartial = async () => {
    const amount = round2(parseFloat(partialAmount) || 0);
    if (getPartialError(amount)) return;
    handleApprove('parcial', amount);
  };

  const handleApprove = async (tipo, chargeValue) => {
    if (!currentCharge) return;
    setLoading(true);
    const user = AuthService.getCurrentUser();

    try {
      const status_charge = tipo === "completo" ? "CANCELADO" : "PARCIAL";
      const payload = {
        id_purchase:  currentCharge.id_purchase,
        charge_id:    currentCharge.id,
        charge_value: chargeValue,
        status_charge,
      };
      const res = await ApiService.updatePurchaseStatusCharge(user.token, payload);
      if (!res || res.status !== "success") {
        Swal.fire("Aviso", (res && res.message) || "No se pudo actualizar el pago", "error");
        return;
      }
      toast.success(
        tipo === "completo" ? "Marcado como Completo ✓" : "Marcado como Parcial ~",
        { autoClose: 2000 }
      );
      goNext();
    } catch (e) {
      toast.error("Error al actualizar el pago");
    } finally {
      setLoading(false);
    }
  };

  // ── Detección automática completo / parcial ────────────────────────────────
  // Solo detecta si el cliente ya envió un monto (value > 0)

  const getPaymentType = (charge) => {
    if (!charge) return null;
    const paid     = parseFloat(charge.value) || 0;
    const total    = parseFloat(charge.Purchase && charge.Purchase.total)    || 0;
    const delivery = parseFloat(charge.Purchase && charge.Purchase.delivery) || 0;
    const fullTotal = total + delivery;
    if (paid === 0 || fullTotal === 0) return null;
    return paid >= fullTotal ? "completo" : "parcial";
  };

  // ── Tabla ──────────────────────────────────────────────────────────────────

  const columns = React.useMemo(
    () => [
      {
        columns: [
          {
            Header: "Id",
            accessor: "id",
            Cell: (row) => <div>{row.original["id"]}</div>,
          },
          {
            Header: "# Orden",
            accessor: "id_purchase",
            Cell: (row) => <div>{row.original["id_purchase"]}</div>,
          },
          {
            Header: "Cliente",
            accessor: "Purchase.User.full_name",
            Cell: (row) => (
              <Link
                to={`${process.env.PUBLIC_URL}/customer-profile/${row.original.Purchase.id_user}`}
                style={{ color: '#007bff', fontWeight: 500 }}
              >
                {row.original.Purchase && row.original.Purchase.User
                  ? row.original.Purchase.User.full_name || row.original.Purchase.User.name
                  : '—'}
              </Link>
            ),
          },
          {
            // Pagos ya aprobados de la orden (no el voucher, que llega en 0)
            Header: "Ya pagado",
            accessor: "order_paid",
            Cell: (row) => (
              <div>S/ {(parseFloat(row.original.order_paid) || 0).toFixed(2)}</div>
            ),
          },
          {
            Header: "Pendiente",
            accessor: "order_pending",
            Cell: (row) => {
              const pending = parseFloat(row.original.order_pending) || 0;
              return (
                <div style={{ color: pending > 0 ? '#e67e22' : '#27ae60', fontWeight: 600 }}>
                  S/ {pending.toFixed(2)}
                </div>
              );
            },
          },
          {
            Header: "Total orden",
            accessor: "Purchase.total",
            Cell: (row) => {
              const t = parseFloat(row.original.Purchase.total)    || 0;
              const d = parseFloat(row.original.Purchase.delivery) || 0;
              return <div>S/ {(t + d).toFixed(2)}</div>;
            },
          },
          {
            Header: "Tipo",
            accessor: "tipo",
            Cell: (row) => {
              const tipo = getPaymentType(row.original);
              if (!tipo) return <span className="badge badge-secondary">-</span>;
              return tipo === "completo"
                ? <span className="badge badge-success">COMPLETO</span>
                : <span className="badge badge-warning">PARCIAL</span>;
            },
          },
          {
            Header: "Fecha pedido",
            accessor: "Purchase.date",
            Cell: (row) => (
              <div>{moment(row.original.Purchase.date).format("YYYY-MM-DD")}</div>
            ),
          },
          {
            Header: "Fecha pago",
            accessor: "date_payment",
            Cell: (row) => (
              <div>
                {row.original.date_payment
                  ? moment(row.original.date_payment).format("YYYY-MM-DD")
                  : "—"}
              </div>
            ),
          },
          {
            Header: "Ver detalle",
            accessor: "voucher",
            Cell: (row) => (
              <Link
                to={`${process.env.PUBLIC_URL}/order-collections/${row.original.Purchase.id}`}
                title="Ver pagos"
              >
                <i
                  className="fa fa-money"
                  style={{ width: 35, fontSize: 20, padding: 11, color: "rgb(222 167 11)" }}
                />
              </Link>
            ),
          },
        ],
      },
    ],
    [charges]
  );

  // ── Render modal de revisión ───────────────────────────────────────────────

  const renderReviewModal = () => {
    const charge = currentCharge;
    if (!charge) return null;

    const paid     = parseFloat(charge.value) || 0;
    const total    = parseFloat(charge.Purchase && charge.Purchase.total)    || 0;
    const delivery = parseFloat(charge.Purchase && charge.Purchase.delivery) || 0;
    const fullTotal = total + delivery;
    const isLast = currentIndex === charges.length - 1;

    // Lo ya pagado y lo pendiente de ESTA orden salen de las deudas del backend
    // (cargos ACTIVO), no del voucher: el voucher del cliente siempre llega con
    // valor 0 y antes el modal mostraba la orden como si no tuviera pagos.
    // Si la orden no está en la lista, es porque ya no debe nada.
    // "Ya pagado" viene de la cola (pagos ACTIVO de la orden). El pendiente se
    // toma de las deudas recién cargadas del cliente; si la orden no está en
    // esa lista (ya pagada o no aprobada) se usa el de la cola.
    const orderStatus  = charge.Purchase ? charge.Purchase.status : null;
    const notApproved  = orderStatus !== 'APROBADO';
    const orderPaid    = round2(parseFloat(charge.order_paid) || 0);
    const orderPending = debtsReady && mainDebt
      ? mainPending
      : round2(parseFloat(charge.order_pending) || 0);
    const fmtAmount    = (n) => `S/ ${n.toFixed(2)}`;

    const totalDebts = userDebts.reduce((s, d) => s + (parseFloat(d.pending) || 0), 0);
    const partialValue      = round2(parseFloat(partialAmount) || 0);
    const partialError      = getPartialError(partialValue);
    const allocationPreview = partialError ? [] : getAllocationPreview(partialValue);

    return (
      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} center>
        {/* Header */}
        <div className="modal-header" style={{ borderBottom: "1px solid #dee2e6", paddingBottom: 12, marginBottom: 16 }}>
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Revisión de Pagos</h5>
            <span className="badge badge-secondary" style={{ fontSize: 14 }}>
              {currentIndex + 1} / {charges.length}
            </span>
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="progress mb-3" style={{ height: 6 }}>
          <div
            className="progress-bar bg-success"
            style={{ width: `${((currentIndex + 1) / charges.length) * 100}%` }}
          />
        </div>

        {/* Info cliente */}
        <div className="row mb-3">
          <div className="col-12 col-sm-6 mb-2 mb-sm-0">
            <small className="text-muted d-block">Cliente</small>
            <strong>{charge.Purchase && charge.Purchase.User && charge.Purchase.User.full_name}</strong>
          </div>
          <div className="col-6 col-sm-3">
            <small className="text-muted d-block">Orden #</small>
            <strong>{charge.id_purchase}</strong>
          </div>
          <div className="col-6 col-sm-3">
            <small className="text-muted d-block">Fecha</small>
            <strong>{moment(charge.Purchase && charge.Purchase.date).format("DD/MM/YYYY")}</strong>
          </div>
        </div>

        {/* Montos de la orden */}
        <div className="row mb-3">
          <div className="col-4">
            <div className="card text-center" style={{ border: "2px solid #28a745" }}>
              <div className="card-body py-2 px-1">
                <small className="text-muted d-block" style={{ fontSize: 11 }}>Ya pagado</small>
                <h5 className="text-success mb-0">{fmtAmount(orderPaid)}</h5>
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="card text-center">
              <div className="card-body py-2 px-1">
                <small className="text-muted d-block" style={{ fontSize: 11 }}>Total orden</small>
                <h5 className="mb-0">S/ {fullTotal.toFixed(2)}</h5>
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className={`card text-center ${orderPending > 0 ? "border-warning" : "border-success"}`}>
              <div className="card-body py-2 px-1">
                <small className="text-muted d-block" style={{ fontSize: 11 }}>Pendiente</small>
                <h5 className={`mb-0 ${orderPending > 0 ? "text-warning" : "text-success"}`}>
                  {fmtAmount(orderPending)}
                </h5>
              </div>
            </div>
          </div>
        </div>

        {/* Qué revisar en el voucher */}
        <div className="alert alert-light text-center mb-3 text-muted" style={{ border: "1px dashed #ccc" }}>
          {notApproved
            ? `La orden no está aprobada (estado: ${orderStatus || '—'}). El reparto no le asignará este pago; apruebe la orden primero o use Omitir.`
            : orderPending > 0
            ? `Revisa la imagen: esta orden debe S/ ${orderPending.toFixed(2)}. Si el voucher cubre ese monto es completo; si no, parcial.`
            : "Esta orden ya no tiene saldo pendiente. Revisa si el voucher corresponde a otra deuda del cliente."}
        </div>

        {/* Imagen voucher */}
        <div className="text-center mb-3" style={{ backgroundColor: "#f8f9fa", borderRadius: 8, padding: 12 }}>
          {charge.voucher_image ? (
            <img
              src={getVoucherUrl(charge.voucher_image)}
              alt="Voucher"
              data-protected="1"
              style={{ maxHeight: 280, maxWidth: "100%", objectFit: "contain", borderRadius: 6 }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="text-muted py-4">
              <i className="fa fa-image fa-3x mb-2 d-block" />
              Sin imagen de voucher
            </div>
          )}
        </div>

        {/* Deudas anteriores del cliente */}
        {loadingDebts && (
          <div className="text-center text-muted mb-2" style={{ fontSize: 13 }}>
            <span className="spinner-border spinner-border-sm mr-1" />
            Cargando deudas anteriores...
          </div>
        )}
        {!loadingDebts && userDebts.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <small className="font-weight-bold text-danger">
                <i className="fa fa-exclamation-triangle mr-1" />
                Otras deudas pendientes del cliente
              </small>
              <span className="badge badge-danger">{userDebts.length} orden(es)</span>
            </div>
            <div
              style={{
                maxHeight: 130,
                overflowY: "auto",
                border: "1px solid #f5c6cb",
                borderRadius: 6,
                padding: "6px 10px",
                backgroundColor: "#fff5f5",
              }}
            >
              {userDebts.map((debt) => (
                <div
                  key={debt.id}
                  className="d-flex justify-content-between align-items-center py-2"
                  style={{ borderBottom: "1px solid #f5c6cb", minHeight: 44 }}
                >
                  <span style={{ fontSize: 13 }}>Orden #{debt.id} &mdash; {moment(debt.date).format("DD/MM/YYYY")}</span>
                  <span className="badge badge-warning" style={{ fontSize: 13, padding: "5px 8px" }}>
                    S/ {(parseFloat(debt.pending) || 0).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="d-flex justify-content-between align-items-center mt-1 pt-1">
                <small className="text-danger font-weight-bold">Total deuda pendiente:</small>
                <small className="text-danger font-weight-bold">S/ {totalDebts.toFixed(2)}</small>
              </div>
            </div>
          </div>
        )}

        {/* Input monto parcial — solo aparece al hacer clic en ~ Parcial */}
        {showPartialInput && (
          <div className="card mb-3" style={{ border: "2px solid #ffc107" }}>
            <div className="card-body py-3">
              <small className="text-muted d-block mb-2 font-weight-bold">
                Monto parcial recibido:
              </small>
              <div className="input-group mb-3">
                <div className="input-group-prepend">
                  <span className="input-group-text" style={{ fontSize: 18 }}>S/</span>
                </div>
                <input
                  type="number"
                  className="form-control"
                  style={{ fontSize: 20, minHeight: 48 }}
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                  autoFocus
                />
              </div>
              {debtsError && (
                <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                  No se pudo cargar la deuda del cliente; el sistema validará el monto al confirmar.
                </div>
              )}
              {partialError && (
                <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{partialError}</div>
              )}
              {allocationPreview.length > 0 && (
                <div className="mb-3" style={{ fontSize: 13 }}>
                  <small className="text-muted d-block mb-1">
                    Se aplicará desde la deuda más antigua:
                  </small>
                  {allocationPreview.map((a) => (
                    <div key={a.id} className="d-flex justify-content-between py-1" style={{ borderBottom: "1px solid #eee" }}>
                      <span>
                        Orden #{a.id}{a.id === charge.id_purchase ? " (este voucher)" : ""}
                      </span>
                      <span>
                        S/ {a.applied.toFixed(2)}{" "}
                        {a.left > 0
                          ? <span className="text-warning">· queda S/ {a.left.toFixed(2)}</span>
                          : <span className="text-success">· pagada</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="row">
                <div className="col-6 pr-1">
                  <button
                    className="btn btn-light btn-block"
                    style={{ minHeight: 48, fontSize: 16 }}
                    onClick={() => setShowPartialInput(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                </div>
                <div className="col-6 pl-1">
                  <button
                    className="btn btn-warning btn-block"
                    style={{ minHeight: 48, fontSize: 16 }}
                    onClick={handleApprovePartial}
                    disabled={loading || (!debtsReady && !debtsError) || !partialAmount || parseFloat(partialAmount) <= 0 || !!partialError}
                  >
                    {loading ? <span className="spinner-border spinner-border-sm mr-1" /> : null}
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Qué hará "Listo" cuando el cliente tiene varias deudas (informativo) */}
        {!showPartialInput && debtsReady && userDebts.length > 0 && (
          <small className="text-muted d-block text-center mb-1">
            "Listo" marcará como pagadas {userDebts.length + (mainDebt ? 1 : 0)} órdenes por S/ {totalPending.toFixed(2)}.
            Si el voucher no cubre ese total, use Parcial.
          </small>
        )}

        {/* Botones de acción */}
        {!showPartialInput && (
          <div className="row mt-2">
            <div className="col-4 pr-1">
              <button
                className="btn btn-light btn-block"
                style={{ minHeight: 48, fontSize: 15 }}
                onClick={goNext}
                disabled={loading}
              >
                {isLast ? "Fin" : "Omitir"}
              </button>
            </div>
            <div className="col-4 px-1">
              <button
                className="btn btn-warning btn-block"
                style={{ minHeight: 48, fontSize: 15 }}
                onClick={() => {
                  setPartialAmount(paid > 0 ? paid.toFixed(2) : '');
                  setShowPartialInput(true);
                }}
                disabled={loading}
              >
                ~ Parcial
              </button>
            </div>
            <div className="col-4 pl-1">
              <button
                className="btn btn-success btn-block"
                style={{ minHeight: 48, fontSize: 15 }}
                onClick={handleApproveComplete}
                disabled={loading || (!debtsReady && !debtsError)}
              >
                {loading ? <span className="spinner-border spinner-border-sm mr-1" /> : null}
                ✓ Listo
              </button>
            </div>
          </div>
        )}
        {showPartialInput && (
          <div className="row mt-2">
            <div className="col-12">
              <button
                className="btn btn-light btn-block"
                style={{ minHeight: 48, fontSize: 15 }}
                onClick={goNext}
                disabled={loading}
              >
                {isLast ? "Finalizar" : "Omitir →"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  // ── Render principal ───────────────────────────────────────────────────────

  return (
    <Fragment>
      <Breadcrumb title="Registro de Pagos cliente" parent="Digital" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
            <h5 className="mb-0">Pagos pendientes de verificación</h5>
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-warning"
                onClick={handleMassDebt}
                disabled={loadingMass}
              >
                {loadingMass
                  ? <span className="spinner-border spinner-border-sm mr-2" />
                  : <i className="fa fa-whatsapp mr-2" />
                }
                Cobro masivo
              </button>
              <button
                className="btn btn-primary"
                onClick={openReview}
                disabled={charges.length === 0}
              >
                <i className="fa fa-play-circle mr-2" />
                Revisar pagos ({charges.length})
              </button>
            </div>
          </div>
          <div className="card-body">
            {loadingTable ? (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
                <p className="mt-3 text-muted">Cargando pagos...</p>
              </div>
            ) : (
              <Table data={charges} columns={columns} />
            )}
          </div>
        </div>
      </div>

      {renderReviewModal()}
      <ToastContainer />
    </Fragment>
  );
};

export default Charge;
